import { getOptionLabel, ProductOptionValues } from '../product/ProductType.ts';
import { mappleIndividualPrice, PaymentMethod } from './constants.ts';

interface CartItem {
  product: {
    name: string;
    boxWeight: string;
  };
  quantity: number;
  purchaseOption: string;
}

interface OrderMessageData {
  orderId: number;
  userName: string;
  address: string;
  city: string;
  timeSlot: string;
  description?: string;
  cartItems: CartItem[];
  total: number;
  paymentMethod: string;
}

export function isMobileDevice() {
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop|BlackBerry/i.test(
    navigator.userAgent
  );
}

/**
 * Normaliza un nombre a "Capital Case" respetando cada palabra.
 * Ej: "juan pablo strah" -> "Juan Pablo Strah"
 * @param name - Nombre completo o parcial
 * @returns Nombre con cada palabra capitalizada
 */
export function capitalizeName(name?: string | null): string {
  if (!name) return '';
  return name
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Formatea un número o string con puntos como separador de miles
 * @param value - Valor a formatear (puede tener o no puntos)
 * @returns String formateado con puntos (ej: "18.000")
 */
export function formatPriceWithDots(value: string | number): string {
  // Convertir a string y remover todo lo que no sea dígito
  const numericValue = String(value).replace(/\D/g, '');

  // Si está vacío, retornar string vacío
  if (!numericValue) return '';

  // Formatear con puntos como separador de miles
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Remueve el formato de puntos de un precio para obtener solo dígitos
 * @param value - Valor formateado con puntos
 * @returns String con solo dígitos (ej: "18000")
 */
export function removePriceFormat(value: string): string {
  return value.replace(/\D/g, '');
}

export function isLogged() {
  const token = localStorage.getItem('token');
  return token !== null && token !== '';
}

export const createWhatsappOrderMessage = ({
  orderId,
  userName,
  address,
  city,
  timeSlot,
  description,
  cartItems,
  total,
  paymentMethod,
}: OrderMessageData) => {
  const requireProof =
    paymentMethod === PaymentMethod.TRANSFER ||
    paymentMethod === PaymentMethod.MERCADOPAGO;

  const message = `🛒 *Nuevo pedido*
🆔 *ID de la orden:* ${orderId}

👤 *Cliente:* ${userName}
🏠 *Dirección:* ${address}, ${city}
🕒 *Horario de recepción:* ${timeSlot.replace('-', ' - ')}
📝 *Detalles:* ${description || 'Sin detalles'}

💳 *Medio de pago:* ${paymentMethod}

📦 *Productos:*
${cartItems
  .map(
    (item) =>
      `• ${item.product.name} – ${item.quantity} x (${
        item.product.boxWeight
      }) – ${getOptionLabel(item.purchaseOption as ProductOptionValues)}`
  )
  .join('\n')}

💰 *Total del pedido:* $${total.toLocaleString('es-AR')}

${
  requireProof
    ? '📩 *Por favor, enviá el comprobante de pago para confirmar el pedido.*\n'
    : ''
}🙌 *Gracias por tu pedido!*`;

  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/5491124912757?text=${encodedMessage}`;
};

export const calculateWeightLabel = (
  boxWeight: string,
  quantity: number,
  purchaseOption: ProductOptionValues | string
): string => {
  if (!boxWeight || !quantity) return '';

  const cleanedWeights = boxWeight
    .replace(/\s/g, '')
    .split(/[-/]/)
    .map((w) => Number(w))
    .filter((w) => !isNaN(w));

  if (cleanedWeights.length === 0) return '';

  const formatWeight = (w: number) =>
    Number.isInteger(w) ? w : parseFloat(w.toFixed(2));

  let label = '';
  switch (purchaseOption) {
    case ProductOptionValues.HALF_CRATE:
      label =
        cleanedWeights.map((w) => formatWeight((w / 2) * quantity)).join('-') +
        ' kg';
      break;

    case ProductOptionValues.UNIT:
      label =
        cleanedWeights.map((w) => formatWeight(w * quantity)).join('-') + ' kg';
      break;

    case ProductOptionValues.PACK:
      label =
        cleanedWeights.map((w) => formatWeight(w * quantity)).join('-') +
        ' paquete';
      break;

    case ProductOptionValues.KG:
    case ProductOptionValues.GR:
    case ProductOptionValues.LT:
      label = ''; // ya se vende por peso/gramos/litros, no mostrar
      break;

    default:
      label =
        cleanedWeights.map((w) => formatWeight(w * quantity)).join('-') + ' kg';
      break;
  }

  return label.trim();
};

export const getUnitPrice = (
  basePrice: string,
  purchaseOption: ProductOptionValues,
  productType?: string,
  specialPrices?: string
): number => {
  if (purchaseOption === ProductOptionValues.MAPLE && specialPrices) {
    const cleanedSpecialPrice = removePriceFormat(specialPrices);
    const parsed = parseInt(cleanedSpecialPrice);
    return isNaN(parsed) ? mappleIndividualPrice : parsed;
  }

  // Remover puntos y caracteres no numéricos antes de parsear
  const cleanedPrice = removePriceFormat(basePrice);
  const price = parseInt(cleanedPrice);
  if (isNaN(price)) return 0;

  if (productType?.toLowerCase() === 'otros') {
    switch (purchaseOption) {
      case ProductOptionValues.MAPLE:
        return mappleIndividualPrice;
      case ProductOptionValues.HALF_CRATE:
        return price / 2;
      case ProductOptionValues.FULL_CRATE:
        return price;
    }
  }

  return purchaseOption === ProductOptionValues.HALF_CRATE ? price / 2 : price;
};
